import { useEffect, useMemo, useState } from 'react';
import { Search } from '@mui/icons-material';
import { Box, capitalize } from '@mui/material';
import { common } from '@mui/material/colors';
import dayjs from 'dayjs';
import { get } from 'lodash';
import { observer } from 'mobx-react-lite';
import { FuseSelect, ListPage, ListRow } from '/src/components';
import { useFilterStore, useLoanStore, useUserStore } from '/src/context';
import { mediumDate } from '/src/lib/constants';
import { Note } from '/src/lib/models';
import { DataModelType, LoanType } from '/src/lib/types';
import { currency, percent } from '/src/utils';

/**
 * List Loans Page
 */
export const ListLoansPage = observer(() => {
  const defaultSortType = 'name';
  const defaultSortReverse = false;

  /** Context **/
  const loanStore = useLoanStore();
  const filterStore = useFilterStore();
  const userStore = useUserStore();

  /** State **/
  const [search, setSearch] = useState<Record<string, any>>({});
  const [sortType, setSortType] = useState(defaultSortType);
  const [sortReverse, setSortReverse] = useState(defaultSortReverse);
  const [notes, setNotes] = useState<Note[]>();
  const [notesName, setNotesName] = useState<string>();

  /** Computed **/
  const loans = loanStore.loans;
  const isFiltered = !!Object.values(search).length;

  const companies = useMemo(() => filterStore.companyListGet(), [filterStore._companyList]);

  const listData = useMemo(() => {
    if (loans) {
      let list = loans.toSorted((a, b) => (get(a, sortType) > get(b, sortType) ? 1 : -1));
      if (sortReverse) list.reverse();

      if (search.companyId) list = list.filter((loan) => loan.borrower?._id === search.companyId);
      if (search.dealType) {
        list = list.filter(
          (loan) =>
            (search.dealType === LoanType.Owned && loan.isOwned) || (search.dealType === LoanType.Loan && !loan.isOwned)
        );
      } else list = [];

      return list;
    }
  }, [loans, search, sortType, sortReverse]);

  /** Methods **/

  const sortList = (type?: string) => {
    if (type) {
      if (sortType === type) setSortReverse(!sortReverse);
      else setSortType(type);
    }
  };

  const showNotesPopup = (notes?: Note[], name?: string) => {
    setNotes(notes ?? []);
    setNotesName(name);
  };

  /** Effects **/

  useEffect(() => {
    loanStore.query(search);
  }, []);

  /** Render **/
  return (
    <ListPage
      data={listData}
      modelType={DataModelType.Loan}
      headerItems={[
        { flex: 5, propName: 'notes', label: 'Notes' },
        { flex: 1, propName: 'name', label: 'Deal Name' },
        { flex: 1, propName: 'borrower.name', label: 'Company' },
        { flex: 1, propName: 'principalBalance', label: 'Current Balance', textAlign: 'right' },
        { flex: 1, propName: 'assetValue', label: 'Current Asset Value', textAlign: 'right' },
        { flex: 1, propName: 'originationFee', label: 'Origination Fee', hide: search.isOwned, textAlign: 'right' },
        { flex: 1, propName: 'adminFee', label: 'Administrative Fee', hide: !search.isOwned, textAlign: 'right' },
        { flex: 1, propName: 'interestRate', label: 'Interest Rate', hide: search.isOwned, textAlign: 'right' },

        // TODO: this is "discountRate" in the header, but "discountFee" in the body (on the List Loans angular view)
        { flex: 1, propName: 'discountFee', label: 'Discount Rate', hide: !search.isOwned, textAlign: 'right' },

        { flex: 1, propName: 'created', label: 'Date Created', textAlign: 'right' },
        { flex: 1, propName: 'type', label: 'Type', textAlign: 'right' },
        { flex: 1, propName: 'status', label: 'Status', textAlign: 'right' },
      ]}
      toolbarItems={
        <>
          {userStore.isAdmin && (
            <Box display="flex" flex={1}>
              <Box display="flex" justifyContent="start" alignItems="center" gap="3px">
                <Search />
                <FuseSelect
                  value={search.companyId}
                  updateValue={(id) => setSearch({ ...search, companyId: id })}
                  options={companies.map((company) => ({ label: company.name, value: company._id }))}
                  placeholder="All Companies"
                  enablePlaceholderOption
                  boxProps={{ className: 'no-margin-top-bottom' }}
                  tooltip="Filter by Company"
                  disableUnderline
                  disableMargin
                  color={common.white}
                />
              </Box>
            </Box>
          )}
          {userStore.isAdmin && (
            <Box display="flex" flex={1}>
              <Box display="flex" justifyContent="start" alignItems="center" gap="3px">
                <Search />
                <FuseSelect
                  value={search.dealType}
                  updateValue={(dealType) => setSearch({ ...search, dealType })}
                  placeholder="Choose a Deal Type..."
                  enablePlaceholderOption
                  options={Object.entries(LoanType).map(([label, value]) => ({ label: `${label} Deals`, value }))}
                  disableUnderline
                  disableMargin
                  color={common.white}
                />
              </Box>
            </Box>
          )}
        </>
      }
      isFiltered={isFiltered}
      noFilteredDataMessage="No Deals found for that Company."
      message={!search.dealType ? 'Please choose a Deal Type from the dropdown.' : undefined}
      sortType={sortType}
      sortReverse={sortReverse}
      sortList={sortList}
      notes={notes}
      notesName={notesName}
      setNotes={setNotes}
      setNotesName={setNotesName}
    >
      {listData?.map((loan, i) => (
        <ListRow
          key={loan._id}
          items={[
            {
              propName: 'notes',
              isNotes: true,
              flex: 5,
              hide: !loan.notes?.length,
              showNotesPopup: () => showNotesPopup(loan.notes, loan.name),
            },
            { propName: 'name', content: loan.name, url: `/deals/${loan._id}` },
            { propName: 'borrower.name', content: loan.borrower.name, url: `/companies/${loan.borrower._id}` },
            { propName: 'principalBalance', content: currency.format(loan.principalBalance), textAlign: 'right' },
            { propName: 'assetValue', content: currency.format(loan.assetValue), textAlign: 'right' },
            {
              propName: 'originationFee',
              content: percent.format(loan.originationFee),
              hide: search.isOwned,
              textAlign: 'right',
            },
            {
              propName: 'adminFee',
              content: loan.adminFee ? currency.format(loan.adminFee) : '',
              hide: !search.isOwned,
              textAlign: 'right',
            },
            {
              propName: 'interestRate',
              content: percent.format(loan.interestRate),
              hide: search.isOwned,
              textAlign: 'right',
            },
            {
              propName: 'discountFee',
              content: loan.discountFee ? percent.format(loan.discountFee) : undefined,
              hide: !search.isOwned,
              textAlign: 'right',
            },
            { propName: 'created', content: dayjs(loan.created).format(mediumDate), textAlign: 'right' },
            { propName: 'type', content: loan.isOwned ? 'Owned' : 'Loan', textAlign: 'right' },
            { propName: 'status', content: capitalize(loan.status), textAlign: 'right' },
          ]}
          rowIndex={i}
        />
      ))}
    </ListPage>
  );
});
